import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosColorPalette } from 'react-icons/io';
import { ImFontSize } from 'react-icons/im';

const BrandKitDisplay = ({
  type,
  items = [],
  isMobile = false,
  onFontChange,  // 폰트 변경 콜백 (index, newValue) => void
  showConfirmModal  // 확인 모달 함수
}) => {
  const { t } = useTranslation();
  // 폰트 편집 상태
  const [editingFontIndex, setEditingFontIndex] = useState(null);
  const [editFontValue, setEditFontValue] = useState('');
  const isColorType = type === 'color';
  const isFontType = type === 'font';

  // 색상 타입일 때의 렌더링
  // 실제 색상 개수만큼만 렌더링 (최소 5개 - 한 줄)
  const colorCount = items.filter(Boolean).length;
  const displayCount = Math.max(colorCount, 5); // 최소 한 줄(5개)

  const renderColorDisplay = () => (
    <div style={{
      flex: isMobile ? 'none' : '2',
      width: isMobile ? '100%' : 'auto',
      background: '#ffffff',
      borderRadius: '16px',
      padding: isMobile ? '16px' : '24px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      border: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      height: isMobile ? 'auto' : 'calc(100vh - 200px)',
      minHeight: isMobile ? 'auto' : '450px',
      maxHeight: isMobile ? 'none' : '600px',
      overflow: 'hidden'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px'
      }}>
        <IoIosColorPalette style={{ fontSize: '24px', color: '#1A62EA' }} />
        <h3 style={{
          fontSize: isMobile ? '16px' : '18px',
          fontWeight: '700',
          color: '#1f2937',
          margin: 0
        }}>
          {t('brandKit.colorPaletteTitle')}
        </h3>
      </div>
      <div style={{
        fontSize: isMobile ? '12px' : '13px',
        color: '#6b7280',
        marginBottom: isMobile ? '12px' : '20px'
      }}>
        {t('brandKit.colorPaletteDesc', { count: colorCount })}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: isMobile ? '12px 0px' : '18px 0px',
        ...(displayCount > 20 ? { flex: 1, overflowY: 'auto', alignContent: 'start' } : {})
      }}>
        {Array.from({ length: displayCount }, (_, index) => {
          const color = items[index];
          const isEmpty = !color;

          return (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {/* 색상 박스 */}
              <div
                style={{
                  width: '75%',
                  aspectRatio: '1',
                  backgroundColor: isEmpty ? '#ffffff' : color,
                  border: isEmpty ? 'none' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isEmpty ? 'default' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isEmpty) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isEmpty) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
              </div>
              {/* Hex 코드 */}
              <div style={{
                fontSize: '12px',
                fontWeight: '500',
                color: isEmpty ? '#d1d5db' : '#374151',
                textTransform: 'uppercase'
              }}>
                {isEmpty ? '' : color}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // 폰트 편집 시작
  const startEditFont = (index) => {
    setEditingFontIndex(index);
    setEditFontValue(items[index] || '');
  };

  // 폰트 편집 저장
  const saveEditFont = async () => {
    if (editingFontIndex === null) return;

    const originalFont = items[editingFontIndex];
    const newFont = editFontValue.trim();

    // 값이 변경되지 않았으면 그냥 닫기
    if (newFont === originalFont) {
      setEditingFontIndex(null);
      setEditFontValue('');
      return;
    }

    // 값이 변경되었으면 확인 모달 표시
    if (showConfirmModal && onFontChange) {
      const confirmed = await showConfirmModal(
        t('brandKit.fontChangeConfirm', { from: originalFont, to: newFont })
      );

      if (confirmed) {
        onFontChange(editingFontIndex, newFont);
      }
    }

    setEditingFontIndex(null);
    setEditFontValue('');
  };

  // 폰트 편집 취소
  const cancelEditFont = () => {
    setEditingFontIndex(null);
    setEditFontValue('');
  };

  // 폰트 타입일 때의 렌더링
  const renderFontDisplay = () => (
    <div style={{
      flex: isMobile ? 'none' : '2',
      width: isMobile ? '100%' : 'auto',
      background: '#ffffff',
      borderRadius: '16px',
      padding: isMobile ? '16px' : '24px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      border: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      height: isMobile ? 'auto' : 'calc(100vh - 200px)',
      minHeight: isMobile ? 'auto' : '450px',
      maxHeight: isMobile ? '300px' : '600px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px'
      }}>
        <ImFontSize style={{ fontSize: '24px', color: '#1A62EA' }} />
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#1f2937',
          margin: 0
        }}>
          {t('brandKit.fontListTitle')}
        </h3>
      </div>
      <div style={{
        fontSize: '13px',
        color: '#6b7280',
        marginBottom: '20px'
      }}>
        {t('brandKit.fontListDesc')}
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        flex: 1,
        overflowY: 'auto'
      }}>
        {Array.from({ length: 25 }, (_, index) => {
          const font = items[index];
          const isEmpty = !font;
          const isEditing = editingFontIndex === index;

          // 편집 중인 경우
          if (isEditing) {
            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center'
                }}
              >
                <input
                  type="text"
                  value={editFontValue}
                  onChange={(e) => setEditFontValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') saveEditFont();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') cancelEditFont();
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #1A62EA',
                    fontSize: '16px',
                    fontWeight: '500',
                    outline: 'none'
                  }}
                  autoFocus
                />
                <button
                  onClick={saveEditFont}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#10b981',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {t('brandKit.save')}
                </button>
                <button
                  onClick={cancelEditFont}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#6b7280',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            );
          }

          // 빈 슬롯이면 렌더링 안함
          if (isEmpty) return null;

          return (
            <div
              key={index}
              onClick={() => startEditFont(index)}
              style={{
                backgroundColor: '#f5f8fb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px 16px',
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: '500',
                cursor: 'pointer',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                color: '#374151',
                fontFamily: font
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#1A62EA';
                e.currentTarget.style.background = '#f0f7ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.background = '#f5f8fb';
              }}
              title={t('brandKit.clickToEditFont')}
            >
              <span>{font}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // 타입에 따른 조건부 렌더링
  if (isColorType) {
    return renderColorDisplay();
  } else if (isFontType) {
    return renderFontDisplay();
  } else {
    return (
      <div style={{
        flex: '2',
        background: '#ffffff',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e5e7eb',
        textAlign: 'center',
        color: '#dc2626'
      }}>
        {t('brandKit.invalidType')}: {type}
      </div>
    );
  }
};

export default BrandKitDisplay;